/*
 * FILE: unified_ui/src/lib/trackEvent.ts
 * PURPOSE: Fire-and-forget UI event tracking → POST /api/chat-event → ChatLayer.
 *          Every event is ONE chronological beat of the user's path, rendered
 *          in the ChatLayer room timeline as a message.event line. Events carry
 *          enough context (query text, selected variant, purpose, runId, ...)
 *          that an operator can follow the whole journey without opening
 *          anything else.
 * OWNS: trackEvent() helper for all SPA interaction tracking.
 * EXPORTS: trackEvent, trackUserFacingError.
 *
 * DIVISION OF LABOUR (no duplicates in the timeline):
 *   - SPA events (this file) cover the UI path: wizard stage movement, exact
 *     choices, back/restart/resume/repeat, manager-help form interactions,
 *     new chat threads, downloads, page/app opens, user-facing errors.
 *   - Core service alerts (botCore/src/bus/coreHandlers.ts) cover business
 *     milestones for ALL platforms: search.prepare / search.start /
 *     search.finalize / search.cancel / manager_help.submit / rating.submit.
 *     The SPA deliberately does NOT re-emit those (would double every line).
 */

type UIEventName =
  | "app-opened"
  | "view-opened"
  | "onboarding-shown"
  | "subscribe-gate-shown"
  | "subscribe-gate-passed"
  | "wizard-step"
  | "order-failed"
  | "manager-help-option"
  | "manager-help-timing"
  | "chat-new-thread"
  | "button-click"
  | "artifact-downloaded";

const EVENT_LABELS: Record<UIEventName, string> = {
  "app-opened": "📱 Приложение открыто",
  "view-opened": "👁 Страница открыта",
  "onboarding-shown": "👋 Экран приветствия",
  "subscribe-gate-shown": "🚫 Экран подписки",
  "subscribe-gate-passed": "✅ Подписка подтверждена",
  "wizard-step": "📋 Шаг мастера",
  "order-failed": "⚠️ Заказ не выполнен",
  "manager-help-option": "📞 Обращение",
  "manager-help-timing": "⏱ Сроки",
  "chat-new-thread": "🆕 Новый чат начат",
  "button-click": "👆 Нажата кнопка",
  "artifact-downloaded": "📄 Скачан файл",
};

/**
 * Wire-format event names carried in meta.event — these are what operators
 * see in ChatLayer (message.event meta variants), so they follow the dotted
 * eventType convention even though the local names are kebab-case.
 */
const WIRE_EVENT: Record<UIEventName, string> = {
  "app-opened": "app-opened",
  "view-opened": "view-opened",
  "onboarding-shown": "onboarding.shown",
  "subscribe-gate-shown": "subscribe_gate.shown",
  "subscribe-gate-passed": "subscribe_gate.passed",
  "wizard-step": "wizard.step",
  "order-failed": "order.failed",
  "manager-help-option": "manager_help.option",
  "manager-help-timing": "manager_help.timing",
  "chat-new-thread": "chat.new_thread",
  "button-click": "ui.button.click",
  "artifact-downloaded": "artifact.downloaded",
};

export type TrackEventMeta = Record<string, unknown>;

/**
 * Serialized fire-and-forget pipeline. Events are POSTed one after another so
 * the ChatLayer room timeline preserves the exact chronological order of the
 * user's path (the operator reads events as a story — reordering would break it).
 * The chain never rejects: a failed POST must not block later events.
 */
import { authHeaders } from './sessionToken'

let pipeline: Promise<void> = Promise.resolve();

function enqueue(payload: Record<string, unknown>) {
  pipeline = pipeline
    .then(() =>
      fetch("/api/chat-event", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(payload),
      }),
    )
    .then(() => undefined)
    .catch(() => {
      // fire-and-forget — never throw on tracking failure
    });
}

/**
 * Track an SPA interaction. Rides the existing /api/chat-event →
 * evt.chat.message.event path (webBridge derives tags from meta.event).
 *
 * @param event  logical event name (see UIEventName)
 * @param detail human-readable detail appended to the text label
 * @param meta   extra structured context: element, view, context, step, runId, ...
 */
export function trackEvent(event: UIEventName, detail?: string, meta?: TrackEventMeta) {
  const text = detail ? `${EVENT_LABELS[event]}: ${detail}` : EVENT_LABELS[event];
  enqueue({
    event: "message.event",
    text,
    meta: { event: WIRE_EVENT[event], detail, ...meta },
  });
}

/**
 * Track a user-facing error surfaced by the SPA (e.g. agent timeout / fallback
 * text). Rides the same /api/chat-event → message.event path with the
 * error.user_facing semantic event in meta — no webBridge changes needed.
 */
export function trackUserFacingError(text: string, meta?: TrackEventMeta) {
  enqueue({
    event: "message.event",
    text,
    meta: { event: "error.user_facing", detail: text, ...meta },
  });
}
